import type { LatLng, OSRMRoute } from '../types';

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export async function fetchOSRMRoute(
  endpoints: LatLng[],
  options?: { alternatives?: boolean },
): Promise<OSRMRoute[]> {
  if (endpoints.length < 2) return [];

  // OSRM expects coordinates in {lon},{lat};{lon},{lat} order
  const coordString = endpoints
    .map((e) => `${e.longitude.toFixed(6)},${e.latitude.toFixed(6)}`)
    .join(';');

  // Alternatives only supported for 2 endpoints
  const wantAlts = options?.alternatives !== false && endpoints.length === 2;
  const url = `https://router.project-osrm.org/route/v1/driving/${coordString}?overview=full&geometries=geojson&alternatives=${wantAlts ? '3' : 'false'}`;

  const delays = [500, 1200];
  let attempt = 0;

  while (attempt < 3) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12_000);

    try {
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timer);

      if (res.ok) {
        const data = await res.json();
        if (data.code === 'Ok' && Array.isArray(data.routes) && data.routes.length > 0) {
          return data.routes as OSRMRoute[];
        }
        if (data.code === 'NoRoute') {
          throw new Error('No driving route found between these points.');
        }
        throw new Error(`Routing error: ${data.message || data.code}`);
      }

      // Retryable errors
      if ([429, 500, 502, 503, 504].includes(res.status)) {
        if (attempt < delays.length) {
          await sleep(delays[attempt]!);
          attempt++;
          continue;
        }
      }

      throw new Error(`Routing HTTP ${res.status}`);
    } catch (err: any) {
      clearTimeout(timer);
      if (err.name === 'AbortError') {
        if (attempt < delays.length) {
          await sleep(delays[attempt]!);
          attempt++;
          continue;
        }
        throw new Error('Routing request timed out.');
      }
      if (attempt < delays.length) {
        await sleep(delays[attempt]!);
        attempt++;
        continue;
      }
      throw err;
    }
  }

  throw new Error('Unable to connect to routing service.');
}
